interface WakeLockSentinel extends EventTarget {
  release(): Promise<void>;
  onrelease: ((this: WakeLockSentinel, ev: Event) => unknown) | null;
  released: boolean;
  type: 'screen';
}

interface NavigatorWithWakeLock extends Navigator {
  wakeLock: {
    request(type: 'screen'): Promise<WakeLockSentinel>;
  };
}

let wakeLockSentinel: WakeLockSentinel | null = null;

export const isWakeLockSupported = (): boolean => {
  const navigatorWithWakeLock = navigator as unknown as NavigatorWithWakeLock;
  return 'wakeLock' in navigator && navigatorWithWakeLock.wakeLock !== undefined;
};

export const requestWakeLock = async (): Promise<boolean> => {
  if (!isWakeLockSupported()) {
    return false;
  }

  try {
    const navigatorWithWakeLock = navigator as unknown as NavigatorWithWakeLock;
    wakeLockSentinel = await navigatorWithWakeLock.wakeLock.request('screen');
    setupWakeLockReacquisition();
    return true;
  } catch {
    return false;
  }
};

export const releaseWakeLock = async (): Promise<boolean> => {
  if (!wakeLockSentinel) {
    return false;
  }

  try {
    await wakeLockSentinel.release();
    wakeLockSentinel = null;
    removeWakeLockReacquisitionListener();
    return true;
  } catch {
    return false;
  }
};

const setupWakeLockReacquisition = (): void => {
  if (!wakeLockSentinel) {
    return;
  }

  wakeLockSentinel.addEventListener('release', handleWakeLockRelease);
  document.addEventListener('visibilitychange', (): void => {
    void handleVisibilityChange();
  });
};

const removeWakeLockReacquisitionListener = (): void => {
  if (wakeLockSentinel) {
    wakeLockSentinel.removeEventListener('release', handleWakeLockRelease);
  }
  document.removeEventListener('visibilitychange', (): void => {
    void handleVisibilityChange();
  });
};

const handleWakeLockRelease = (): void => {
  wakeLockSentinel = null;
};

const handleVisibilityChange = async (): Promise<void> => {
  if (document.hidden || wakeLockSentinel) {
    return;
  }

  await requestWakeLock();
};


